import { HttpStatus } from "#/shared/api/http";

export const MAX_RETRIES = 2;
export const RETRY_BASE_DELAY_MS = 300;
export const RETRY_MAX_DELAY_MS = 3_000;

const RETRYABLE_STATUSES = new Set([
  HttpStatus.TOO_MANY_REQUESTS,
  HttpStatus.BAD_GATEWAY,
  HttpStatus.SERVICE_UNAVAILABLE,
  HttpStatus.GATEWAY_TIMEOUT,
]);

export function isRetryableStatus(status) {
  return RETRYABLE_STATUSES.has(status);
}

// A timer that rejects early if the caller's signal aborts, so a superseded wait
// stops between attempts instead of running to completion.
export function abortableSleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
      return;
    }

    const onAbort = () => {
      clearTimeout(timer);
      reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
    };

    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);

    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

// Jittered exponential backoff: `baseMs * 2 ** attempt`, capped at `maxMs`, then
// spread across `[capped * jitterFloor, capped)`. `jitterFloor` 0 gives full
// jitter (`random * capped`); 0.5 gives half jitter (`capped/2 + random*capped/2`).
//
// @param {Number} attempt - Zero-based retry index.
// @param {Object} options
// @param {Number} options.baseMs - Base delay before exponential growth.
// @param {Number} options.maxMs - Delay ceiling the exponential is capped at.
// @param {Number} [options.jitterFloor=0] - Lower bound of the jitter window as a
//   fraction of the capped delay.
// @returns {Number} Delay in milliseconds.
export function jitteredBackoff(attempt, { baseMs, maxMs, jitterFloor = 0 }) {
  const capped = Math.min(baseMs * 2 ** attempt, maxMs);
  const floor = capped * jitterFloor;

  return floor + Math.random() * (capped - floor);
}
